/**
 * Widget stylesheet, injected into the shadow root so it never collides with
 * host-page styles. Mirrors the CrossCountry Mortgage layout (centered column,
 * bordered option cards, thin progress bar, black pill Continue button).
 * Brand colors are CSS custom properties overridable via config.brand.
 */
export const CSS = /* css */ `
:host {
  --frs-fg: #111417;
  --frs-muted: #6b7280;
  --frs-border: #d7dbe0;
  --frs-border-hover: #111417;
  --frs-bg: #ffffff;
  --frs-primary: #111417;
  --frs-primary-fg: #ffffff;
  --frs-accent: #1a56db;
  --frs-track: #e7eaee;
  --frs-radius: 10px;
  --frs-maxw: 640px;
  all: initial;
}
*, *::before, *::after { box-sizing: border-box; }
.frs-llf {
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--frs-fg);
  background: var(--frs-bg);
  line-height: 1.4;
  -webkit-font-smoothing: antialiased;
  width: 100%;
}
.frs-llf button { font-family: inherit; cursor: pointer; }

/* Header */
.frs-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; padding: 18px 24px; border-bottom: 1px solid #eef0f2;
}
.frs-logo { height: 34px; width: auto; display: block; }
.frs-logo-text { font-weight: 800; font-size: 18px; letter-spacing: -0.02em; }
.frs-header-right { display: flex; align-items: center; gap: 14px; }
.frs-phone { display: inline-flex; align-items: center; gap: 6px; color: var(--frs-fg); text-decoration: none; font-weight: 700; font-size: 14px; }
.frs-phone svg { width: 16px; height: 16px; }
@media (max-width: 560px){ .frs-phone span { display: none; } }

/* Body */
.frs-body { max-width: var(--frs-maxw); margin: 0 auto; padding: 28px 24px 56px; }
.frs-back {
  display: inline-flex; align-items: center; gap: 4px; background: none; border: 0;
  color: var(--frs-fg); font-weight: 700; font-size: 15px; padding: 6px 0; margin-bottom: 18px;
}
.frs-back svg { width: 18px; height: 18px; }
.frs-back[hidden] { display: none; }

/* Progress */
.frs-progress { margin-bottom: 40px; }
.frs-progress-track { height: 8px; background: var(--frs-track); border-radius: 999px; overflow: hidden; }
.frs-progress-fill { height: 100%; background: var(--frs-primary); border-radius: 999px; transition: width .35s ease; }
.frs-progress-label { font-size: 13px; font-weight: 700; margin-top: 6px; }

/* Question */
.frs-question {
  font-size: clamp(28px, 4vw, 40px); font-weight: 800; text-align: center;
  letter-spacing: -0.02em; line-height: 1.15; margin: 8px 0 28px;
  padding-bottom: 2px;
  background-image: linear-gradient(96deg, #60EAF4 0%, var(--frs-accent) 100%);
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent; color: transparent;
}
.frs-note { text-align: center; color: var(--frs-muted); font-size: 14px; margin: -16px 0 24px; }

/* Option cards */
.frs-choices { display: flex; flex-direction: column; gap: 14px; max-width: 440px; margin: 0 auto; }
.frs-choice {
  display: flex; align-items: center; gap: 14px; width: 100%; text-align: left;
  background: var(--frs-bg); border: 1px solid var(--frs-border); border-radius: var(--frs-radius);
  padding: 18px 20px; font-size: 17px; font-weight: 600; color: var(--frs-fg);
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.frs-choice:hover, .frs-choice:focus-visible { border-color: var(--frs-border-hover); box-shadow: 0 0 0 1px var(--frs-border-hover) inset; outline: none; }
.frs-choice[aria-pressed="true"] { border-color: var(--frs-primary); box-shadow: 0 0 0 2px var(--frs-primary) inset; }
.frs-choice svg { width: 26px; height: 26px; flex: 0 0 auto; color: var(--frs-fg); }
.frs-choice.frs-yesno { justify-content: center; text-align: center; padding: 16px; font-size: 18px; }

/* Yes/No compact row */
.frs-yesno-row { display: flex; gap: 14px; max-width: 440px; margin: 0 auto; }
.frs-yesno-row .frs-choice { flex: 1; }

/* Inputs */
.frs-fields { max-width: 440px; margin: 0 auto; display: flex; flex-direction: column; gap: 18px; }
.frs-field-row { display: flex; gap: 14px; }
.frs-field { display: flex; flex-direction: column; gap: 8px; flex: 1; }
.frs-label { font-weight: 700; font-size: 15px; }
.frs-input {
  border: 1px solid var(--frs-border); border-radius: var(--frs-radius); padding: 14px 16px;
  font-size: 16px; color: var(--frs-fg); background: var(--frs-bg); width: 100%;
}
.frs-input:focus { outline: none; border-color: var(--frs-primary); box-shadow: 0 0 0 2px rgba(17,20,23,.12); }
.frs-input.frs-invalid { border-color: #dc2626; }
.frs-input-error { color: #dc2626; font-size: 13px; font-weight: 600; }
.frs-input-prefix { position: relative; }
.frs-input-prefix .frs-prefix { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: var(--frs-muted); font-weight: 700; }
.frs-input-prefix .frs-input { padding-left: 30px; }

/* Loan officer select */
.frs-lo-select { max-width: 440px; margin: 18px auto 0; }
.frs-select { width: 100%; border: 1px solid var(--frs-border); border-radius: var(--frs-radius); padding: 14px 16px; font-size: 16px; background: var(--frs-bg); color: var(--frs-fg); }

/* Consent */
.frs-consent { max-width: 440px; margin: 22px auto 0; display: flex; gap: 10px; align-items: flex-start; }
.frs-consent input { margin-top: 3px; flex: 0 0 auto; width: 18px; height: 18px; }
.frs-consent label { font-size: 11px; color: var(--frs-muted); line-height: 1.5; }

/* Continue / submit */
.frs-actions { max-width: 440px; margin: 26px auto 0; }
.frs-continue {
  width: 100%; background: var(--frs-primary); color: var(--frs-primary-fg); border: 0;
  border-radius: var(--frs-radius); padding: 16px; font-size: 15px; font-weight: 800;
  letter-spacing: .06em; text-transform: uppercase; display: inline-flex; align-items: center; justify-content: center; gap: 10px;
}
.frs-continue:disabled { opacity: .55; cursor: not-allowed; }
.frs-skip { display: block; margin: 16px auto 0; background: #6b7280; color: #fff; border: 0; border-radius: var(--frs-radius); padding: 12px 26px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; font-size: 13px; }
.frs-resend { display: inline-flex; align-items: center; gap: 8px; background: none; border: 0; color: var(--frs-fg); font-weight: 700; }

/* Spinner */
.frs-spin { animation: frs-rotate 0.8s linear infinite; }
@keyframes frs-rotate { to { transform: rotate(360deg); } }

/* Success */
.frs-success { text-align: center; max-width: 440px; margin: 0 auto; }
.frs-success svg { width: 64px; height: 64px; color: #16a34a; }
.frs-success h2 {
  font-size: 28px; font-weight: 800; margin: 16px 0 8px; padding-bottom: 2px;
  background-image: linear-gradient(96deg, #60EAF4 0%, var(--frs-accent) 100%);
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent; color: transparent;
}
.frs-success p { color: var(--frs-muted); font-size: 16px; }

/* Footer */
.frs-footer { border-top: 1px solid #eef0f2; padding: 20px 24px; color: var(--frs-muted); font-size: 12px; text-align: center; }

/* Popup overlay (applied on the host element when opened as modal) */
`;

/** Styles for the popup overlay — these live in the light DOM (host page) so the
 *  overlay can cover the viewport. Namespaced to avoid collisions. */
export const OVERLAY_CSS = /* css */ `
.frs-llf-overlay {
  position: fixed; inset: 0; z-index: 2147483000; display: flex; align-items: stretch; justify-content: center;
  background: rgba(17,20,23,.55); backdrop-filter: blur(2px); overflow-y: auto;
  animation: frs-fade .2s ease;
}
.frs-llf-overlay-inner { position: relative; width: 100%; max-width: 900px; margin: auto; background: #fff; min-height: 100%; }
@media (min-width: 720px){ .frs-llf-overlay-inner { min-height: auto; margin: 32px auto; border-radius: 14px; overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,.35); } }
.frs-llf-overlay-close {
  position: absolute; top: 14px; right: 14px; z-index: 5; width: 40px; height: 40px; border-radius: 999px;
  border: 0; background: #f1f3f5; color: #111417; display: inline-flex; align-items: center; justify-content: center;
}
.frs-llf-overlay-close:hover { background: #e4e7eb; }
html.frs-llf-locked, body.frs-llf-locked { overflow: hidden !important; }
@keyframes frs-fade { from { opacity: 0; } to { opacity: 1; } }
`;
